package org.catharsis.spendtel.feature.presentation.auth

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.navigation.NavHostController
import org.catharsis.spendtel.core.navigation.AppRoutes
import org.catharsis.spendtel.core.navigation.resolvePostAuthDestination
import org.catharsis.spendtel.core.navigation.showsWebLandingPage
import org.koin.compose.viewmodel.koinViewModel

enum class AuthMode {
    SignIn,
    Register,
}

class AuthFormController(
    val authState: AuthenticationState,
    val isRegisterMode: Boolean,
    val email: String,
    val password: String,
    val confirmPassword: String,
    val passwordVisible: Boolean,
    val acceptedTerms: Boolean,
    val errorMessage: String?,
    val infoMessage: String?,
    val canSubmit: Boolean,
    val canUseGoogleSignIn: Boolean,
    val onEmailChange: (String) -> Unit,
    val onPasswordChange: (String) -> Unit,
    val onConfirmPasswordChange: (String) -> Unit,
    val onPasswordVisibilityToggle: () -> Unit,
    val onAcceptedTermsChange: (Boolean) -> Unit,
    val submit: () -> Unit,
    val switchMode: (register: Boolean) -> Unit,
    val onGoogleSignIn: () -> Unit,
    val onForgotPassword: () -> Unit,
)

@Composable
fun rememberAuthFormController(
    navHostController: NavHostController,
    authViewModel: AuthenticationViewModel = koinViewModel(),
): AuthFormController {
    val state by authViewModel.authState.collectAsState()

    var authMode by rememberSaveable { mutableStateOf(AuthMode.SignIn) }
    var email by rememberSaveable { mutableStateOf("") }
    var password by rememberSaveable { mutableStateOf("") }
    var confirmPassword by rememberSaveable { mutableStateOf("") }
    var passwordVisible by rememberSaveable { mutableStateOf(false) }
    var acceptedTerms by rememberSaveable { mutableStateOf(false) }
    var errorMessage by rememberSaveable { mutableStateOf<String?>(null) }
    var infoMessage by rememberSaveable { mutableStateOf<String?>(null) }

    LaunchedEffect(Unit) {
        authViewModel.effect.collect { effect ->
            when (effect) {
                is AuthenticationEffect.OnAuthSuccess -> {
                    errorMessage = null
                    val popRoute = if (showsWebLandingPage()) {
                        AppRoutes.Landing.name
                    } else {
                        AppRoutes.Authentication.name
                    }
                    navHostController.navigate(resolvePostAuthDestination(effect.user)) {
                        popUpTo(popRoute) { inclusive = true }
                    }
                }
                is AuthenticationEffect.OnSignInError -> {
                    errorMessage = effect.message
                    infoMessage = null
                }
                is AuthenticationEffect.OnRegisterError -> {
                    errorMessage = effect.message
                    infoMessage = null
                }
                is AuthenticationEffect.OnForgotPasswordRequestUpdate -> {
                    infoMessage = if (effect.success) {
                        "Password reset email sent. Check your inbox."
                    } else {
                        "Unable to send reset email. Please try again."
                    }
                    errorMessage = null
                }
                else -> Unit
            }
        }
    }

    val isRegisterMode = authMode == AuthMode.Register
    val passwordsMatch = password == confirmPassword
    val canSubmit = email.isNotBlank() &&
        password.length >= 6 &&
        (!isRegisterMode || (passwordsMatch && acceptedTerms))
    val canUseGoogleSignIn = !isRegisterMode || acceptedTerms

    fun submit() {
        errorMessage = null
        infoMessage = null
        if (!canSubmit) {
            errorMessage = when {
                email.isBlank() -> "Enter your email address."
                password.length < 6 -> "Password must be at least 6 characters."
                isRegisterMode && !passwordsMatch -> "Passwords do not match."
                isRegisterMode && !acceptedTerms -> "Accept the Terms & Conditions and Privacy Policy to continue."
                else -> "Please check your details and try again."
            }
            return
        }

        if (isRegisterMode) {
            authViewModel.postEvent(AuthenticationEvent.OnRegister(email.trim(), password))
        } else {
            authViewModel.postEvent(AuthenticationEvent.OnSignIn(email.trim(), password))
        }
    }

    fun switchMode(register: Boolean) {
        authMode = if (register) AuthMode.Register else AuthMode.SignIn
        errorMessage = null
        infoMessage = null
        confirmPassword = ""
        acceptedTerms = false
    }

    fun googleSignIn() {
        if (!canUseGoogleSignIn) {
            errorMessage = "Accept the Terms & Conditions and Privacy Policy to continue."
            return
        }
        authViewModel.postEvent(AuthenticationEvent.OnGoogleSignIn)
    }

    return AuthFormController(
        authState = state,
        isRegisterMode = isRegisterMode,
        email = email,
        password = password,
        confirmPassword = confirmPassword,
        passwordVisible = passwordVisible,
        acceptedTerms = acceptedTerms,
        errorMessage = errorMessage,
        infoMessage = infoMessage,
        canSubmit = canSubmit,
        canUseGoogleSignIn = canUseGoogleSignIn,
        onEmailChange = {
            email = it
            errorMessage = null
        },
        onPasswordChange = {
            password = it
            errorMessage = null
        },
        onConfirmPasswordChange = {
            confirmPassword = it
            errorMessage = null
        },
        onPasswordVisibilityToggle = { passwordVisible = !passwordVisible },
        onAcceptedTermsChange = {
            acceptedTerms = it
            errorMessage = null
        },
        submit = ::submit,
        switchMode = ::switchMode,
        onGoogleSignIn = ::googleSignIn,
        onForgotPassword = {
            if (email.isBlank()) {
                errorMessage = "Enter your email to reset your password."
            } else {
                authViewModel.postEvent(AuthenticationEvent.OnForgotPassword(email.trim()))
            }
        },
    )
}
